import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigationStore } from './useNavigationStore.js'
import { useShopStore } from './useShopStore.js'
import { useProgressionStore } from './useProgressionStore.js'
import { useAuthStore } from './useAuthStore.js'
import { DailyPackButton } from './DailyPackButton.js'
import { FeedbackButton } from './FeedbackButton.js'
import { QUEST_DEFINITIONS } from '../data/questDefinitions.js'
import { S } from '../locales/strings.js'

export const MainMenu: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigationStore((s) => s.navigate)
  const setGameMode = useNavigationStore((s) => s.setGameMode)
  const level = useProgressionStore((s) => s.level)
  const questProgress = useProgressionStore((s) => s.questProgress)
  const session = useAuthStore((s) => s.session)
  const logout = useAuthStore((s) => s.logout)

  const visibleQuests = React.useMemo(() => {
    const daily = QUEST_DEFINITIONS.filter((d) => d.type === 'daily')
    const weekly = QUEST_DEFINITIONS.filter((d) => d.type === 'weekly')
    const achievements = QUEST_DEFINITIONS.filter((d) => d.type === 'achievement')
    return [...daily, ...weekly, ...achievements].slice(0, 3)
  }, [])

  const [loadingBtn, setLoadingBtn] = React.useState<string | null>(null)

  const handlePlayPvP = () => {
    if (loadingBtn !== null) return
    setLoadingBtn('pvp')
    setGameMode('pvp')
    navigate('matchmaking')
    setTimeout(() => setLoadingBtn(null), 1500)
  }

  const handlePlayBot = () => {
    if (loadingBtn !== null) return
    setLoadingBtn('bot')
    setGameMode('bot')
    navigate('deck-selector')
    setTimeout(() => setLoadingBtn(null), 1500)
  }

  return (
    <div
      role="main"
      aria-busy={loadingBtn !== null}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        padding: 24,
        paddingTop: 'max(env(safe-area-inset-top, 0px), 24px)',
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #0a0814 0%, #12082a 60%, #0a0a18 100%)',
        position: 'relative',
      }}
    >

      {/* Level badge — top right */}
      <div style={{
        position: 'absolute',
        top: 'max(env(safe-area-inset-top, 0px), 16px)',
        right: 20,
        background: 'linear-gradient(135deg, #C9A84C, #9B6FE8)',
        color: '#fff',
        fontFamily: 'monospace',
        fontWeight: 900,
        fontSize: 13,
        padding: '5px 11px',
        borderRadius: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
      }}>
        Lv. {level}
      </div>

      <h1 style={{ color: '#C9A84C', fontSize: 28, margin: '8px 0 4px', fontFamily: 'monospace', textShadow: '0 0 24px rgba(201,168,76,0.5)' }}>
        {t(S.MAIN_MENU_TITLE)}
      </h1>

      {/* Auth pill */}
      {session === null ? (
        <button
          aria-label="Anmelden oder Registrieren"
          onClick={() => navigate('login')}
          style={{
            background: 'rgba(155,111,232,0.18)',
            border: '1px solid #9B6FE8',
            borderRadius: 20,
            color: '#9B6FE8',
            fontFamily: 'monospace',
            fontSize: 13,
            padding: '5px 16px',
            cursor: 'pointer',
            minHeight: 36,
          }}
        >
          {t(S.MAIN_MENU_LOGIN_REGISTER)}
        </button>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            aria-label={`Eingeloggt als ${session.displayName}`}
            style={{
              background: 'rgba(74,170,58,0.18)',
              border: '1px solid #4AAA3A',
              borderRadius: 20,
              color: '#4AAA3A',
              fontFamily: 'monospace',
              fontSize: 13,
              padding: '5px 14px',
            }}
          >
            👤 {session.displayName}
          </span>
          <button
            aria-label="Abmelden"
            onClick={() => logout()}
            style={{
              background: 'none',
              border: '1px solid #555',
              borderRadius: 20,
              color: '#888',
              fontFamily: 'monospace',
              fontSize: 12,
              padding: '5px 10px',
              cursor: 'pointer',
              minHeight: 36,
            }}
          >
            {t(S.MAIN_MENU_LOGOUT)}
          </button>
        </div>
      )}

      {/* Quest widget */}
      <section
        aria-label="Aktive Quests"
        style={{
          background: 'rgba(0,0,0,0.65)',
          border: '1px solid #3a2d60',
          borderRadius: 10,
          padding: '14px 16px',
          width: '100%',
          maxWidth: BTN_WIDTH,
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontSize: 12, color: '#C9A84C', marginBottom: 10, fontFamily: 'monospace', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
          {t(S.QUEST_WIDGET_TITLE)}
        </div>
        {visibleQuests.map((q) => {
          const saved = questProgress.find((p) => p.questId === q.id)
          const progress = saved?.progress ?? 0
          const goal = q.target
          const pct = Math.min(100, goal > 0 ? (progress / goal) * 100 : 0)
          return (
            <div key={q.id} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                <div style={{ fontSize: 13, color: '#ddd', fontFamily: 'monospace' }}>{q.title}</div>
                <div style={{ fontSize: 11, color: '#888', fontFamily: 'monospace', marginLeft: 8, whiteSpace: 'nowrap' }}>
                  {progress}/{goal}
                </div>
              </div>
              <div
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={goal}
                aria-label={`${q.title}: ${progress} von ${goal}`}
                style={{ height: 5, background: '#2a2240', borderRadius: 3 }}
              >
                <div style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: pct >= 100 ? '#4AAA3A' : '#3A8AE8',
                  borderRadius: 3,
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          )
        })}
        <button
          aria-label="Alle Quests anzeigen"
          style={{
            background: 'none',
            border: 'none',
            color: '#9B6FE8',
            fontFamily: 'monospace',
            fontSize: 12,
            cursor: 'pointer',
            padding: '4px 0 0',
            marginTop: 2,
          }}
          onClick={() => navigate('progression')}
        >
          {t(S.QUEST_WIDGET_ALL_LINK)}
        </button>
      </section>

      {/* All buttons in a fixed-width container for uniform sizing */}
      <nav aria-label="Hauptnavigation" style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: BTN_WIDTH }}>

        {/* Primary PvP button — visually distinct */}
        <button
          aria-label="PvP-Match gegen einen echten Gegner suchen"
          aria-disabled={loadingBtn === 'pvp'}
          onClick={handlePlayPvP}
          disabled={loadingBtn === 'pvp'}
          className={loadingBtn === 'pvp' ? 'btn-loading' : undefined}
          style={pvpBtnStyle}
        >
          {loadingBtn === 'pvp' ? '...' : t(S.MAIN_MENU_PLAY_PVP)}
        </button>

        <button
          aria-label="Gegen Bot spielen"
          aria-disabled={loadingBtn === 'bot'}
          onClick={handlePlayBot}
          disabled={loadingBtn === 'bot'}
          className={loadingBtn === 'bot' ? 'btn-loading' : undefined}
          style={btnStyle}
        >
          {loadingBtn === 'bot' ? '...' : t(S.MAIN_MENU_PLAY)}
        </button>
        <button aria-label="Solo-Kampagne spielen" onClick={() => navigate('campaign')} style={btnStyle}>
          {t(S.MAIN_MENU_CAMPAIGN)}
        </button>
        <button aria-label="Koop-Match suchen" onClick={() => navigate('coop-matchmaking')} style={btnStyle}>
          {t(S.MAIN_MENU_COOP)}
        </button>
        <button aria-label="Deck bearbeiten" onClick={() => navigate('deck-builder')} style={btnStyle}>
          {t(S.MAIN_MENU_DECK_BUILDER)}
        </button>
        <button aria-label="Meine Decks verwalten" onClick={() => navigate('deck-selector')} style={btnStyle}>
          {t(S.MAIN_MENU_MY_DECKS)}
        </button>
        <button aria-label="Shop öffnen" onClick={() => useShopStore.getState().openShop()} style={btnStyle}>
          {t(S.MAIN_MENU_SHOP)}
        </button>
        <button aria-label="Kartensammlung anzeigen" onClick={() => navigate('inventory')} style={btnStyle}>
          {t(S.MAIN_MENU_INVENTORY)}
        </button>
        <button
          aria-label="Fortschritt und Quests anzeigen"
          onClick={() => navigate('progression')}
          style={{ ...btnStyle, background: 'rgba(155,111,232,0.15)', border: '2px solid #9B6FE8', color: '#C9A84C' }}
        >
          {t(S.MAIN_MENU_PROFILE)}
        </button>
        <button aria-label="Spielerprofil anzeigen" onClick={() => navigate('profile')} style={btnStyle}>
          {t(S.PROFILE_TITLE)}
        </button>
        <button aria-label="Rangliste anzeigen" onClick={() => navigate('leaderboard')} style={btnStyle}>
          {t(S.LEADERBOARD_TITLE)}
        </button>
        <button aria-label="Freundesliste öffnen" onClick={() => navigate('friends')} style={btnStyle}>
          {t(S.FRIENDS_TITLE)}
        </button>
        <button aria-label="Replays anzeigen" onClick={() => navigate('replay')} style={btnStyle}>
          {t(S.REPLAY_TITLE)}
        </button>
        <button aria-label="Laufende Spiele zuschauen" onClick={() => navigate('spectator-browser')} style={btnStyle}>
          {t(S.SPECTATOR_TITLE)}
        </button>
        <button aria-label="Turnier-Modus öffnen" onClick={() => navigate('tournament')} style={btnStyle}>
          {t(S.TOURNAMENT_TITLE)}
        </button>
        <button aria-label="Clan öffnen" onClick={() => navigate('clan')} style={btnStyle}>
          {t(S.CLAN_TITLE)}
        </button>
        <button aria-label="Einstellungen öffnen" onClick={() => navigate('settings')} style={btnStyle}>
          {t(S.MAIN_MENU_SETTINGS)}
        </button>

        <DailyPackButton />
        <FeedbackButton style={{ width: '100%', marginTop: 4 }} />
      </nav>
    </div>
  )
}

const BTN_WIDTH = 300

const btnStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 0',
  fontSize: 16,
  borderRadius: 8,
  border: '2px solid #3a3050',
  background: 'rgba(20,16,32,0.9)',
  color: '#fff',
  cursor: 'pointer',
  minHeight: 44,
  fontFamily: 'monospace',
  transition: 'border-color 0.15s, background 0.15s',
  boxSizing: 'border-box',
}

const pvpBtnStyle: React.CSSProperties = {
  ...btnStyle,
  padding: '14px 0',
  fontSize: 18,
  fontWeight: 900,
  border: '2px solid #C9A84C',
  background: 'linear-gradient(135deg, rgba(201,168,76,0.22), rgba(201,168,76,0.08))',
  color: '#C9A84C',
  boxShadow: '0 0 16px rgba(201,168,76,0.30)',
  letterSpacing: 1,
}
